import React, { useEffect, useState } from 'react';
import { ArrowRight, Mountain, Cake, PartyPopper, Gift, Star } from 'lucide-react';
import type { LoggedInUser } from './LoginScreen';
function isBirthdayToday(birthday: string): boolean {
  const today = new Date();
  const [month, day] = birthday.split('-').map(Number);
  return today.getMonth() + 1 === month && today.getDate() === day;
}
// Confetti particle component
function ConfettiParticle({
  delay,
  left,
  color
}: {
  delay: number;
  left: string;
  color: string;
}) {
  return <div className="absolute w-2.5 h-2.5 rounded-sm opacity-0" style={{
    left,
    top: '-10px',
    backgroundColor: color,
    animation: `confettiFall 3s ${delay}s ease-in infinite`,
    transform: `rotate(${Math.random() * 360}deg)`
  }} data-id="element-1120" />;
}
const CONFETTI_COLORS = ['#FF6B6B', '#FFE66D', '#4ECDC4', '#45B7D1', '#96CEB4', '#FF8A5C', '#D4A5FF', '#FF61A6'];
interface WelcomeScreenProps {
  user: LoggedInUser;
  onContinue: () => void;
}
export function WelcomeScreen({
  user,
  onContinue
}: WelcomeScreenProps) {
  const [visible, setVisible] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [showBirthday, setShowBirthday] = useState(false);
  const hasBirthday = isBirthdayToday(user.birthday);
  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), 100);
    const t2 = setTimeout(() => setShowUser(true), 500);
    const t3 = setTimeout(() => setShowBirthday(true), 1100);
    const t4 = setTimeout(() => setShowMessage(true), hasBirthday ? 2000 : 1100);
    const t5 = setTimeout(() => setShowButton(true), hasBirthday ? 2600 : 1700);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, [hasBirthday]);
  return <div className={`min-h-screen w-full flex flex-col items-center justify-center transition-opacity duration-700 overflow-y-auto py-8 relative ${visible ? 'opacity-100' : 'opacity-0'} ${hasBirthday ? 'bg-gradient-to-br from-[#FF6B6B] via-[#FFE66D] to-[#4ECDC4]' : 'bg-gradient-to-br from-[#F4A460] via-[#FDBA74] to-[#FDE68A]'}`} data-id="element-1121">
      {/* Confetti animation styles */}
      {hasBirthday && <style data-id="element-1122">{`
          @keyframes confettiFall {
            0% { opacity: 1; top: -10px; transform: rotate(0deg) translateX(0); }
            25% { transform: rotate(90deg) translateX(15px); }
            50% { transform: rotate(180deg) translateX(-15px); }
            75% { transform: rotate(270deg) translateX(10px); }
            100% { opacity: 0; top: 100vh; transform: rotate(360deg) translateX(-5px); }
          }
          @keyframes sparkle {
            0%, 100% { opacity: 0.3; transform: scale(0.8); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}</style>}

      {/* Confetti particles */}
      {hasBirthday && <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1123">
          {Array.from({
        length: 30
      }).map((_, i) => <ConfettiParticle key={i} delay={Math.random() * 3} left={`${Math.random() * 100}%`} color={CONFETTI_COLORS[i % CONFETTI_COLORS.length]} data-id="element-1124" />)}
        </div>}

      {/* Decorative background */}
      <div className="absolute inset-0 opacity-5" data-id="element-1125">
        <div className="absolute top-10 left-10 w-40 h-40 border-4 border-white rounded-full" data-id="element-1126" />
        <div className="absolute bottom-20 right-20 w-60 h-60 border-4 border-white rounded-full" data-id="element-1127" />
        <div className="absolute top-1/3 right-1/4 w-24 h-24 border-4 border-white rounded-full" data-id="element-1128" />
      </div>

      {/* Sparkle decorations for birthday */}
      {hasBirthday && <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-1129">
          {[{
        top: '10%',
        left: '15%',
        delay: '0s'
      }, {
        top: '20%',
        left: '80%',
        delay: '0.5s'
      }, {
        top: '60%',
        left: '10%',
        delay: '1s'
      }, {
        top: '70%',
        left: '85%',
        delay: '1.5s'
      }, {
        top: '30%',
        left: '5%',
        delay: '0.3s'
      }, {
        top: '80%',
        left: '50%',
        delay: '0.8s'
      }].map((pos, i) => <Star key={i} size={16} className="absolute text-yellow-200 fill-yellow-200" style={{
        ...pos,
        animation: `sparkle 2s ${pos.delay} ease-in-out infinite`
      }} data-id="element-1130" />)}
        </div>}

      <div className="relative flex flex-col items-center max-w-4xl px-8" data-id="element-1131">
        {/* Company Logo */}
        <div className={`flex items-center gap-3 mb-8 transition-all duration-700 ${visible ? 'translate-y-0 opacity-100' : '-translate-y-8 opacity-0'}`} data-id="element-1132">
          <Mountain className="w-12 h-12 text-white" strokeWidth={1.5} data-id="element-1133" />
          <div data-id="element-1134">
            <h1 className="text-white text-2xl font-bold tracking-wide drop-shadow-md" data-id="element-1135">
              ERPLogiPark Pvt. Ltd.
            </h1>
            <p className="text-white/70 text-xs font-medium" data-id="element-1136">
              Integrated Logistics & Warehouse Management
            </p>
          </div>
        </div>

        {/* Logged-in User Highlight */}
        <div className={`flex flex-col items-center mb-6 transition-all duration-700 ${showUser ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-90'}`} data-id="element-1137">
          <div className={`relative w-32 h-32 rounded-full bg-white shadow-2xl overflow-hidden p-1 mb-4 ${hasBirthday ? 'ring-4 ring-yellow-300' : 'ring-4 ring-white/50'}`} data-id="element-1138">
            <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user.seed}&backgroundColor=transparent`} alt={user.name} className="w-full h-full rounded-full object-cover" data-id="element-1139" />
            {hasBirthday && <div className="absolute -top-2 -right-2 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white" data-id="element-1140">
                <Cake size={18} className="text-white" data-id="element-1141" />
              </div>}
          </div>
          <h2 className="text-white text-4xl font-bold drop-shadow-lg" data-id="element-1142">
            Welcome, {user.name}!
          </h2>
          <span className="text-white/80 text-sm font-medium mt-2 bg-white/20 px-5 py-1.5 rounded-full" data-id="element-1143">
            {user.role}
          </span>
        </div>

        {/* Birthday Wish Section */}
        {hasBirthday && <div className={`text-center mb-6 transition-all duration-700 ${showBirthday ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-90'}`} data-id="element-1144">
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-8 py-6 border border-white/30 shadow-xl" data-id="element-1145">
              <div className="flex items-center justify-center gap-3 mb-3" data-id="element-1146">
                <PartyPopper size={28} className="text-yellow-200" data-id="element-1147" />
                <h3 className="text-white text-3xl font-bold drop-shadow-lg" data-id="element-1148">
                  🎂 Happy Birthday! 🎂
                </h3>
                <PartyPopper size={28} className="text-yellow-200 scale-x-[-1]" data-id="element-1149" />
              </div>
              <p className="text-white/90 text-lg font-medium drop-shadow-md mb-2" data-id="element-1150">
                Wishing you a wonderful birthday, {user.name}!
              </p>
              <p className="text-white/75 text-sm" data-id="element-1151">
                May this special day bring you joy, success, and happiness. 🎉
              </p>
              <div className="flex items-center justify-center gap-4 mt-4" data-id="element-1152">
                <Gift size={20} className="text-pink-200" data-id="element-1153" />
                <span className="text-white/60 text-xs font-medium" data-id="element-1154">
                  From the entire ERPLogiPark Family
                </span>
                <Gift size={20} className="text-pink-200" data-id="element-1155" />
              </div>
            </div>
          </div>}

        {/* Welcome Message - only when NOT birthday */}
        {!hasBirthday && <div className={`text-center mb-10 transition-all duration-700 ${showMessage ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-1156">
            <h3 className="text-white text-2xl font-bold mb-3 drop-shadow-lg leading-tight" data-id="element-1157">
              Welcome to ERPLogiPark's Family
            </h3>
            <div className="w-20 h-1 bg-white/50 rounded-full mx-auto mb-3" data-id="element-1158" />
            <p className="text-white/85 text-lg font-medium drop-shadow-md" data-id="element-1159">
              Thank you for Choosing us.
            </p>
          </div>}

        {/* Continue Button */}
        <div className={`transition-all duration-700 ${showButton ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-1160">
          <button onClick={onContinue} className="group flex items-center gap-3 bg-white text-orange-600 px-8 py-3.5 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300" data-id="element-1161">
            Continue to Dashboard
            <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" data-id="element-1162" />
          </button>
        </div>

        <p className="text-white/50 text-xs mt-8 font-medium" data-id="element-1163">
          {new Date().toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
          {' • '}
          {new Date().toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit'
        })}
        </p>
      </div>
    </div>;
}